import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Panel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

const SCOPES = [
  ["admission", "Admission required documents",
   "Documents collected during student admission (birth cert, vaccination, etc.)"],
  ["employee", "Employee onboarding documents",
   "Documents collected during staff joining."],
  ["vendor", "Vendor onboarding documents",
   "Documents collected from vendor partners."],
];

const SUBJECT_OPTIONS = {
  admission: [
    { value: "student", label: "Student" },
    { value: "father", label: "Father" },
    { value: "mother", label: "Mother" },
    { value: "guardian", label: "Guardian" },
    { value: "pickup_person", label: "Authorised pickup person" },
  ],
  employee: [
    { value: "employee", label: "Employee" },
  ],
  vendor: [
    { value: "vendor", label: "Vendor" },
  ],
};


export default function DocumentsConfig() {
  const { user } = useAuth();
  const [scope, setScope] = useState("admission");
  const [items, setItems] = useState([]);
  const [source, setSource] = useState(null);
  const [categories, setCategories] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const canEdit = ["super_admin", "franchise_hq_admin", "hq_admin",
    "branch_admin", "principal"].includes(user?.role);

  async function load() {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get(`/documents/requirements/${scope}`),
        api.get("/documents/categories"),
      ]);
      setItems(r.data.items || []);
      setSource(r.data.source || "default");
      setCategories(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  function addItem() {
    setItems([...items, {
      subject_kind: SUBJECT_OPTIONS[scope][0].value,
      document_type: "other",
      label: "",
      mandatory: true,
      expiry_required: false,
      file_required: true,
      active: true,
    }]);
  }

  function patch(idx, key, val) {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: val };
    setItems(next);
  }

  function remove(idx) {
    setItems(items.filter((_, i) => i !== idx));
  }

  async function save() {
    setSaving(true);
    try {
      await api.put(`/documents/requirements/${scope}`, {
        scope,
        items,
        branch_id: null, // by default, branch_admin will get their own scope server-side
      });
      toast.success("Required documents saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="documents-config-page">
      <PageHeader
        eyebrow="Documents"
        title="Required documents configuration"
        subtitle="Decide which documents are mandatory for admissions, employees and vendors. Branch admins set their own branch override; franchise HQ sets franchise defaults; super admin sets platform defaults."
        action={
          <Button
            onClick={save}
            disabled={saving || !canEdit || items.length === 0}
            className="bg-terracotta hover:bg-terracotta/90 text-white"
            data-testid="save-requirements-btn"
          >
            <FloppyDisk size={16} /> {saving ? "Saving…" : "Save requirements"}
          </Button>
        }
      />

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="scope-tabs">
        {SCOPES.map(([s, label]) => (
          <button
            key={s}
            onClick={() => setScope(s)}
            className={`px-3 py-2 rounded-full text-sm font-semibold border ${
              scope === s
                ? "bg-terracotta text-white border-terracotta"
                : "bg-paper text-ink-soft border-border"
            }`}
            data-testid={`scope-${s}`}
          >
            {label}
          </button>
        ))}
      </div>

      <Panel
        title={SCOPES.find(([s]) => s === scope)?.[1]}
        right={
          <div className="text-xs text-ink-soft">
            Source: <span className="font-semibold capitalize">{source}</span>
          </div>
        }
      >
        <p className="text-sm text-ink-soft mb-4">
          {SCOPES.find(([s]) => s === scope)?.[2]}
        </p>

        {loading ? (
          <div className="py-12 text-center text-ink-soft text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-ink-soft text-sm">
            No requirements yet. Click "Add document requirement" to start.
          </div>
        ) : (
          <div className="space-y-3" data-testid="requirements-list">
            {items.map((it, idx) => (
              <div
                key={idx}
                className="grid sm:grid-cols-12 gap-2 p-3 rounded-xl border border-border bg-paper"
                data-testid={`requirement-row-${idx}`}
              >
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Applies to</label>
                  <select
                    value={it.subject_kind}
                    onChange={(e) => patch(idx, "subject_kind", e.target.value)}
                    className="w-full mt-1 p-2 rounded-lg border border-border bg-white text-sm"
                  >
                    {SUBJECT_OPTIONS[scope].map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-3">
                  <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Document type</label>
                  <select
                    value={it.document_type}
                    onChange={(e) => patch(idx, "document_type", e.target.value)}
                    className="w-full mt-1 p-2 rounded-lg border border-border bg-white text-sm"
                  >
                    {(categories?.categories?.[scope === "admission" ? (it.subject_kind === "student" ? "student" : "parent") : scope] || []).map((t) => (
                      <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-3">
                  <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Label (shown to user)</label>
                  <Input
                    value={it.label}
                    onChange={(e) => patch(idx, "label", e.target.value)}
                    placeholder="e.g. Birth Certificate"
                  />
                </div>
                <div className="sm:col-span-3 flex items-end gap-3 text-sm flex-wrap">
                  <label className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={it.mandatory !== false}
                      onChange={(e) => patch(idx, "mandatory", e.target.checked)}
                    /> Mandatory
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={!!it.expiry_required}
                      onChange={(e) => patch(idx, "expiry_required", e.target.checked)}
                    /> Expiry
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={it.file_required !== false}
                      onChange={(e) => patch(idx, "file_required", e.target.checked)}
                    /> File upload
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={it.active !== false}
                      onChange={(e) => patch(idx, "active", e.target.checked)}
                    /> Active
                  </label>
                </div>
                <div className="sm:col-span-1 flex items-end justify-end">
                  <button
                    onClick={() => remove(idx)}
                    className="p-2 text-ink-soft hover:text-danger"
                    data-testid={`remove-req-${idx}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {canEdit && (
          <Button
            variant="outline"
            onClick={addItem}
            className="mt-4"
            data-testid="add-requirement-btn"
          >
            <Plus size={16} /> Add document requirement
          </Button>
        )}
      </Panel>
    </div>
  );
}
